import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { gql, useMutation, useApolloClient } from '@apollo/client';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import BasicDetails from './BasicDetails';
import BudgetDetails from './BudgetDetails';
// Import ServiceState to ensure type consistency
import ScopeConfig, { ServiceState } from './ScopeConfig';
import { LabelFilterPair } from './LabelFilterMenu';
import AlertSettings from './AlertSettings';
import LabelSuccessToast from './LabelSuccessToast';

// --- GQL MUTATIONS AND QUERIES ---
const CREATE_BUDGET_MUTATION = gql`
  mutation CreateBudget($input: CreateBudgetInput!) {
    createBudget(input: $input) {
      id
      name
    }
  }
`;

const GET_USERS_BY_ROLE = gql`
  query GetTenantMembers($tenantId: ID!) {
    usersByTenant(tenantId: $tenantId) {
      user {
        id
        name
        email
        photo
      }
      roleInTenant
    }
  }
`;

// --- TYPE DEFINITIONS ---
interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
}

type AlertRecipient = {
  data: { id?: string; name: string; [key: string]: any };
  type: 'role' | 'user';
};

interface RoleData {
  name: string;
  description: string;
  icon: string;
  color: string;
}

interface Tag {
  label: string;
  value: string;
  id: string | number;
}

interface AppOutletContext {
  currentTenant?: { id: string; name: string; };
}

// --- MODIFIED ---
const getInitialService = (): Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] } => ({
  dataSources: [],
  projectIds: [],
  regions: [],
  serviceTypes: [],
  label: [],
  cluster: [],
  namespace: [],
  deployment: [],
  skus: [], // Added skus for LANGFUSE
});

// --- COMPONENT ---
const AddBudgetDialog: React.FC = () => {
  const navigate = useNavigate();
  const { currentTenant } = useOutletContext<AppOutletContext>();
  const paperRef = useRef(null);
  
  const client = useApolloClient();
const staticRoles: RoleData[] = [
  { name: 'Admin', description: 'All current/ new users with admin role', icon: '/icons/admin.svg', color: '#FF8EN00' },
  { name: 'Editor', description: 'All current/ new users with editor role', icon: '/icons/editor.svg', color: '#49A7E4' },
  { name: 'Viewer', description: 'All current/ new users with viewer role', icon: '/icons/viewer.svg', color: '#666666' },
];
  const [createBudget, { loading: mutationLoading }] = useMutation(CREATE_BUDGET_MUTATION);

  // --- COMPONENT STATE ---
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [budgetName, setBudgetName] = useState('');
  const [budgetDescription, setBudgetDescription] = useState('');
  const [tags, setTags] = useState<Tag[]>([]);
  const [provider, setProvider] = useState('GCP');
  // const [services, setServices] = useState<ServiceState[]>([]);
  const [services, setServices] = useState<ServiceState[]>([
    {
      id: '',
      dataSources: [],
      projectIds: [],
      regions: [],
      serviceTypes: [],
      label: [],
      cluster: [],
      namespace: [],
      deployment: [],
      skus: [],
    },
  ]);
  const [period, setPeriod] = useState<'Monthly' | 'Quarterly' | 'Yearly'>('Monthly');
  const [currency, setCurrency] = useState<'INR' | 'USD'>('USD');
  const [amount, setAmount] = useState('');
  const [includeCredits, setIncludeCredits] = useState(false);
  const [thresholds, setThresholds] = useState<{ id: number; percentage: string; }[]>([]);
  const [alertRecipients, setAlertRecipients] = useState<AlertRecipient[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogLabel, setDialogLabel] = useState('');
  const [dialogTag, setDialogTag] = useState('');
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [lastDeletedTag, setLastDeletedTag] = useState<Tag | null>(null);
  const [allUsers, setAllUsers] = useState<UserData[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);
  const [metric, setMetric] = useState<'COST' | 'TOKENS'>('COST'); // State for LANGFUSE metric
  
  useEffect(() => {
    if (!currentTenant?.id) return;

    const fetchAllUsers = async () => {
      setUsersLoading(true);
      const rolesToFetch = ['ADMIN', 'EDITOR', 'VIEWER'];

      try {
        const promises = rolesToFetch.map(role => 
          client.query({
            query: GET_USERS_BY_ROLE,
            variables: { tenantId: currentTenant.id, roles: [role] }
          })
        );
        
        const results = await Promise.all(promises);        
        const combinedUsers = results.flatMap(result => result.data.usersByTenant);
        const uniqueUsersMap = new Map();
        combinedUsers.forEach(({ user, roleInTenant }) => {
            if (user && user.id && !uniqueUsersMap.has(user.id)) {
                uniqueUsersMap.set(user.id, {
                    id: user.id,
                    name: user.name,
                    email: user.email,
                    role: roleInTenant, 
                    avatar: user.photo   
                });
            }
        });        
        setAllUsers(Array.from(uniqueUsersMap.values()));

      } catch (error) {
        console.error("Failed to fetch users for dropdown:", error);
        setAllUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchAllUsers();
  }, [currentTenant?.id, client]);
  
  useEffect(() => {
    const newErrors = { ...errors };
    let hasErrorChanged = false;

    if (amount.trim() && newErrors.amount) {
        delete newErrors.amount;
        hasErrorChanged = true;
    }
    if (provider && newErrors.provider) {
        delete newErrors.provider;
        hasErrorChanged = true;
    }
    if (services.length > 0 && (newErrors.dataSources || newErrors.regions || newErrors.services)) {
        delete newErrors.dataSources;
        delete newErrors.regions;
        delete newErrors.services;
        hasErrorChanged = true;
    }
    if (alertRecipients.length > 0 && newErrors.alertRecipients) {
        delete newErrors.alertRecipients;
        hasErrorChanged = true;
    }

    if (hasErrorChanged) {
        setErrors(newErrors);
    }
  }, [amount, provider, services, alertRecipients, errors]);

  const handleBudgetNameChange = (name: string) => {
    setBudgetName(name);
    const newErrors = { ...errors };

    if (newErrors.budgetName) {
        delete newErrors.budgetName;
    }

    if (!/^[a-zA-Z0-9\s_-]*$/.test(name)) {
      newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
    }
    setErrors(newErrors);
  };

  const handleBudgetDescriptionChange = (description: string) => {
    setBudgetDescription(description);
    const newErrors = { ...errors };

    if(newErrors.budgetDescription) {
        delete newErrors.budgetDescription;
    }

    if (description.length > 200) {
      newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
    }
    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    const trimmedName = budgetName.trim();
    if (!trimmedName) {
      newErrors.budgetName = 'Budget Name is required.';
    } else if (trimmedName.length < 3) {
      newErrors.budgetName = 'Name must be at least 3 characters.';
    } else if (trimmedName.length > 50) {
      newErrors.budgetName = 'Name cannot exceed 50 characters.';
    } else if (!/^[a-zA-Z0-9\s_-]+$/.test(trimmedName)) {
      newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
    }

    const trimmedDescription = budgetDescription.trim();
    if (!trimmedDescription) {
      newErrors.budgetDescription = 'Budget Description is required.';
    } else if (trimmedDescription.length > 200) {
      newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
    }

    if (!provider) newErrors.provider = 'A Cloud Provider must be selected.';
    if (services.length === 0 || !services.every(s => s.dataSources.length > 0)) newErrors.dataSources = 'Each service must have a Data Source.';
    
    // Specific validations for GCP
    if (provider === 'GCP') {
      if (!services.every(s => s.regions.length > 0)) newErrors.regions = 'Each service must have a Region.';
      if (!services.every(s => s.serviceTypes.length > 0)) newErrors.services = 'Each service must have a Service Type configured.';
    }
    
    if (!amount.trim()) newErrors.amount = 'Budget Amount is required.';
    if (alertRecipients.length === 0) newErrors.alertRecipients = 'At least one Alert Recipient is required.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !currentTenant?.id) return;

    const createBudgetInput = {
      tenantId: currentTenant.id,
      name: budgetName.trim(),
      description: budgetDescription.trim(),
      tags: tags.reduce((obj, item) => ({ ...obj, [item.label]: item.value }), {}),
      provider: provider,
      period: period.toUpperCase() as 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
      amount: parseFloat(amount.replace(/,/g, '')),
      includeCredits: provider === 'LANGFUSE' ? false : includeCredits,
      alertThresholds: thresholds.map(t => parseInt(t.percentage, 10)).filter(t => !isNaN(t)),
      metric: provider === 'LANGFUSE' ? metric : "COST",
      
      userIdsToNotify: alertRecipients.map(recipient => {
        if (recipient.type === 'role') {
          return `ROLE:${recipient.data.name.toLowerCase()}`;
        }
        return recipient.data.id as string;
      }),

      scopeConfigurations: services.map(service => ({
        dataSourceIds: service.dataSources,
        projectIds: provider === 'GCP' ? service.projectIds : [],
        regions: provider === 'GCP' ? service.regions : [],
        services: service.serviceTypes,
        labels: provider === 'GCP' ? service.label.flatMap((l: LabelFilterPair) =>
            l.values.map((v: string) => ({ key: l.key, value: v }))
        ) : [],
        k8sClusters: provider === 'GCP' ? service.cluster : [],
        k8sNamespaces: provider === 'GCP' ? service.namespace : [],
        skus: provider === 'LANGFUSE' ? service.skus : [],
      })),
    };

    try {
      const result = await createBudget({
        variables: { input: createBudgetInput },
      });

      if (result.data?.createBudget) {
        navigate('/budget-alerts', {
          replace: true,
          state: { budgetAdded: true, budgetName: result.data.createBudget.name }
        });
      }
    } catch (err) {
      console.error("Failed to create budget:", err);
      setIsErrorToastOpen(true); 
    }
  };

  const handleAddTag = () => {
    const newTag: Tag = { id: Date.now(), label: dialogLabel, value: dialogTag };
    if (newTag.label && newTag.value && !tags.some(tag => tag.label === newTag.label && tag.value === newTag.value)) {
      setTags(prevTags => [...prevTags, newTag]);
    }
    setIsDialogOpen(false);
    setDialogLabel('');
    setDialogTag('');
  };

  const handleDeleteTag = (tagToDelete: Tag) => {
    setLastDeletedTag(tagToDelete);
    setTags(prevTags => prevTags.filter(tag => tag.id !== tagToDelete.id));
    setIsToastOpen(true);
  };
  
  const handleUndo = () => {
    if (lastDeletedTag) {
      setTags(prevTags => [...prevTags, lastDeletedTag]);
      setLastDeletedTag(null);
      setIsToastOpen(false);
    }
  };

  const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setIsToastOpen(false);
    setLastDeletedTag(null);
  };
  
  const handleErrorToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsErrorToastOpen(false);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setDialogLabel('');
    setDialogTag('');
  };
  
  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, height: '100%', boxSizing: 'border-box', backgroundColor: '#F8F9FA' }}>
      <LabelSuccessToast open={isToastOpen} onClose={handleToastClose} message="Label deleted successfully" onUndo={handleUndo} />
      <Snackbar
        open={isErrorToastOpen}
        autoHideDuration={6000}
        onClose={handleErrorToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleErrorToastClose} severity="error" variant="filled" sx={{ width: '100%' }}>
          We are unable to add budget at this moment. Please try again after some time.
        </Alert>
      </Snackbar>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: -1 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: '#333333', ml: -1 }}>
            <ArrowBackIcon sx={{ width: '20px', height: '20px' }} />
          </IconButton>
          <Typography variant="body2" color="#333333" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '14px' }}>
            Add New Budget
          </Typography>
        </Box>
        <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 2.5, mt: -1 }}>
          Add budget to Keep your cloud spending on track with a custom budget.
        </Typography>
      </Box>
      
      <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        
        <BasicDetails
            budgetName={budgetName}
            onBudgetNameChange={handleBudgetNameChange}
            budgetDescription={budgetDescription}
            onBudgetDescriptionChange={handleBudgetDescriptionChange}
            tags={tags}
            handleDeleteTag={handleDeleteTag}
            setIsDialogOpen={setIsDialogOpen}
            errors={errors}
        />

        <ScopeConfig
            provider={provider}
            setProvider={setProvider}
            services={services}
            setServices={setServices}
            getInitialService={getInitialService}
            errors={errors}
            period={period} 
        />

        <BudgetDetails
          period={period}
          setPeriod={setPeriod}
          currency={currency}
          setCurrency={setCurrency}
          amount={amount}
          setAmount={setAmount}
          includeCredits={includeCredits}
          setIncludeCredits={setIncludeCredits}
          thresholds={thresholds}
          setThresholds={setThresholds}
          errors={errors}
          provider={provider}
          metric={metric}
          setMetric={setMetric}
        />

        <AlertSettings 
            recipients={alertRecipients}
            setRecipients={setAlertRecipients}
            errors={errors}
            allUsers={allUsers}
            isLoading={usersLoading}
            roles={staticRoles}
        />

        <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => navigate(-1)} sx={{ width: '79px', height: '36px', borderRadius: '8px', backgroundColor: '#F2F7FF', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#E0EFFF' } }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={mutationLoading}
              sx={{ width: '112px', height: '36px', borderRadius: '8px', backgroundColor: '#0064FF', color: 'white', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#0053D9' } }}
            >
              {mutationLoading ? <CircularProgress size={24} color="inherit" /> : 'Add Budget'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog 
        open={isDialogOpen} 
        onClose={handleCloseDialog}
        PaperProps={{
          ref: paperRef,
          sx: {
            borderRadius: '12px', width: '600px', height: '322px',
            maxWidth: '600px', overflow: 'hidden',
            backgroundImage: `url('/textures/Texture.svg'), url('/textures/Gradient_Popup.svg')`,
            backgroundRepeat: 'no-repeat, no-repeat',
            backgroundPosition: 'top right, center', backgroundSize: 'auto, cover',
          }
        }}
      >
       <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <img src="/icons/add-filter-icon1.svg" alt="Add" style={{ width: '56px', height: '56px', marginRight: '16px' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px', color: '#333333', fontFamily: 'Inter' }}>
                Add Label/ Tag
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400,fontSize: '14px', color: '#666666', fontFamily: 'Inter' }}>
                Add label details to setup alerts.
              </Typography>
            </Box>
          </Box>
          <DialogContent sx={{ p: '0 !important', mt: 7, mb: 3, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>
                  Key<span style={{color: 'red'}}>*</span>
                </Typography>
                <TextField
                  fullWidth variant="outlined" placeholder="Enter label"
                  value={dialogLabel} onChange={(e) => setDialogLabel(e.target.value)} size="small"
                  InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogLabel && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }}
                  sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0', }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px', }, }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>
                  Value<span style={{color: 'red'}}>*</span>
                </Typography>
                <TextField
                  fullWidth variant="outlined" placeholder="Enter tag"
                  value={dialogTag} onChange={(e) => setDialogTag(e.target.value)} size="small"
                   InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogTag && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }}
                  sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '& fieldset': { borderColor: '#E0E0E0', }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px',}, }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <Button onClick={handleCloseDialog} sx={{ width:"79px", height:"36px",textTransform: 'none', borderRadius: '8px', color: '#0064FF', backgroundColor: '#F2F7FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}>Cancel</Button>
            <Button 
              variant="contained" onClick={handleAddTag} disabled={!dialogLabel || !dialogTag}
              sx={{ width:"74px", height:"36px",textTransform: 'none', borderRadius: '8px', backgroundColor: '#0064FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}
            > Add </Button>
          </DialogActions> 
        </Box>
      </Dialog>
    </Box>
  );
};

export default AddBudgetDialog;



// import React, { useState, useEffect, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   Button,
//   IconButton,
//   TextField,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   InputAdornment,
//   CircularProgress,
//   Snackbar,
//   Alert,
// } from '@mui/material';
// import { useNavigate, useOutletContext } from 'react-router-dom';
// import { gql, useMutation, useApolloClient } from '@apollo/client';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import CheckIcon from '@mui/icons-material/Check';
// import BasicDetails from './BasicDetails';
// import BudgetDetails from './BudgetDetails';
// // Import ServiceState to ensure type consistency
// import ScopeConfig, { ServiceState } from './ScopeConfig';
// import { LabelFilterPair } from './LabelFilterMenu';
// import AlertSettings from './AlertSettings';
// import LabelSuccessToast from './LabelSuccessToast';

// // --- GQL MUTATIONS AND QUERIES ---
// const CREATE_BUDGET_MUTATION = gql`
//   mutation CreateBudget($input: CreateBudgetInput!) {
//     createBudget(input: $input) {
//       id
//       name
//     }
//   }
// `;

// const GET_USERS_BY_ROLE = gql`
//   query GetTenantMembers($tenantId: ID!) {
//     usersByTenant(tenantId: $tenantId) {
//       user {
//         id
//         name
//         email
//         photo
//       }
//       roleInTenant
//     }
//   }
// `;

// // --- TYPE DEFINITIONS ---
// interface UserData {
//   id: string;
//   name: string;
//   email: string;
//   role: string;
//   avatar?: string;
// }

// type AlertRecipient = {
//   data: { id?: string; name: string; [key: string]: any };
//   type: 'role' | 'user';
// };

// interface RoleData {
//   name: string;
//   description: string;
//   icon: string;
//   color: string;
// }

// interface Tag {
//   label: string;
//   value: string;
//   id: string | number;
// }

// interface AppOutletContext {
//   currentTenant?: { id: string; name: string; };
// }

// // --- FIXED ---
// const getInitialService = (): Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] } => ({
//   dataSources: [],
//   projectIds: [], // <-- FIX: This line was missing
//   regions: [],
//   serviceTypes: [],
//   label: [],
//   cluster: [],
//   namespace: [],
//   deployment: []
// });

// // --- COMPONENT ---
// const AddBudgetDialog: React.FC = () => {
//   const navigate = useNavigate();
//   const { currentTenant } = useOutletContext<AppOutletContext>();
//   const paperRef = useRef(null);
  
//   const client = useApolloClient();
// const staticRoles: RoleData[] = [
//   { name: 'Admin', description: 'All current/ new users with admin role', icon: '/icons/admin.svg', color: '#FF8900' },
//   { name: 'Editor', description: 'All current/ new users with editor role', icon: '/icons/editor.svg', color: '#49A7E4' },
//   { name: 'Viewer', description: 'All current/ new users with viewer role', icon: '/icons/viewer.svg', color: '#666666' },
// ];
//   const [createBudget, { loading: mutationLoading }] = useMutation(CREATE_BUDGET_MUTATION);

//   // --- COMPONENT STATE ---
//   const [errors, setErrors] = useState<{ [key: string]: string }>({});
//   const [budgetName, setBudgetName] = useState('');
//   const [budgetDescription, setBudgetDescription] = useState('');
//   const [tags, setTags] = useState<Tag[]>([]);
//   const [provider, setProvider] = useState('');
//   const [services, setServices] = useState<ServiceState[]>([]);
//   const [period, setPeriod] = useState<'Monthly' | 'Quarterly' | 'Yearly'>('Monthly');
//   const [currency, setCurrency] = useState<'INR' | 'USD'>('USD');
//   const [amount, setAmount] = useState('');
//   const [includeCredits, setIncludeCredits] = useState(false);
//   const [thresholds, setThresholds] = useState<{ id: number; percentage: string; }[]>([]);
//   const [alertRecipients, setAlertRecipients] = useState<AlertRecipient[]>([]);
//   const [isDialogOpen, setIsDialogOpen] = useState(false);
//   const [dialogLabel, setDialogLabel] = useState('');
//   const [dialogTag, setDialogTag] = useState('');
//   const [isToastOpen, setIsToastOpen] = useState(false);
//   const [lastDeletedTag, setLastDeletedTag] = useState<Tag | null>(null);
//   const [allUsers, setAllUsers] = useState<UserData[]>([]);
//   const [usersLoading, setUsersLoading] = useState(true);
//   const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);
  
//   useEffect(() => {
//     if (!currentTenant?.id) return;

//     const fetchAllUsers = async () => {
//       setUsersLoading(true);
//       const rolesToFetch = ['ADMIN', 'EDITOR', 'VIEWER'];

//       try {
//         const promises = rolesToFetch.map(role => 
//           client.query({
//             query: GET_USERS_BY_ROLE,
//             variables: { tenantId: currentTenant.id, roles: [role] }
//           })
//         );
        
//         const results = await Promise.all(promises);        
//         const combinedUsers = results.flatMap(result => result.data.usersByTenant);
//         const uniqueUsersMap = new Map();
//         combinedUsers.forEach(({ user, roleInTenant }) => {
//             if (user && user.id && !uniqueUsersMap.has(user.id)) {
//                 uniqueUsersMap.set(user.id, {
//                     id: user.id,
//                     name: user.name,
//                     email: user.email,
//                     role: roleInTenant, 
//                     avatar: user.photo   
//                 });
//             }
//         });        
//         setAllUsers(Array.from(uniqueUsersMap.values()));

//       } catch (error) {
//         console.error("Failed to fetch users for dropdown:", error);
//         setAllUsers([]);
//       } finally {
//         setUsersLoading(false);
//       }
//     };

//     fetchAllUsers();
//   }, [currentTenant?.id, client]);
  
//   useEffect(() => {
//     const newErrors = { ...errors };
//     let hasErrorChanged = false;

//     if (amount.trim() && newErrors.amount) {
//         delete newErrors.amount;
//         hasErrorChanged = true;
//     }
//     if (provider && newErrors.provider) {
//         delete newErrors.provider;
//         hasErrorChanged = true;
//     }
//     if (services.length > 0 && (newErrors.dataSources || newErrors.regions || newErrors.services)) {
//         delete newErrors.dataSources;
//         delete newErrors.regions;
//         delete newErrors.services;
//         hasErrorChanged = true;
//     }
//     if (alertRecipients.length > 0 && newErrors.alertRecipients) {
//         delete newErrors.alertRecipients;
//         hasErrorChanged = true;
//     }

//     if (hasErrorChanged) {
//         setErrors(newErrors);
//     }
//   }, [amount, provider, services, alertRecipients, errors]);

//   const handleBudgetNameChange = (name: string) => {
//     setBudgetName(name);
//     const newErrors = { ...errors };

//     if (newErrors.budgetName) {
//         delete newErrors.budgetName;
//     }

//     if (!/^[a-zA-Z0-9\s_-]*$/.test(name)) {
//       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
//     }
//     setErrors(newErrors);
//   };

//   const handleBudgetDescriptionChange = (description: string) => {
//     setBudgetDescription(description);
//     const newErrors = { ...errors };

//     if(newErrors.budgetDescription) {
//         delete newErrors.budgetDescription;
//     }

//     if (description.length > 200) {
//       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
//     }
//     setErrors(newErrors);
//   };

//   const validateForm = () => {
//     const newErrors: { [key: string]: string } = {};
    
//     const trimmedName = budgetName.trim();
//     if (!trimmedName) {
//       newErrors.budgetName = 'Budget Name is required.';
//     } else if (trimmedName.length < 3) {
//       newErrors.budgetName = 'Name must be at least 3 characters.';
//     } else if (trimmedName.length > 50) {
//       newErrors.budgetName = 'Name cannot exceed 50 characters.';
//     } else if (!/^[a-zA-Z0-9\s_-]+$/.test(trimmedName)) {
//       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
//     }

//     const trimmedDescription = budgetDescription.trim();
//     if (!trimmedDescription) {
//       newErrors.budgetDescription = 'Budget Description is required.';
//     } else if (trimmedDescription.length > 200) {
//       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
//     }

//     if (!provider) newErrors.provider = 'A Cloud Provider must be selected.';
//     if (services.length === 0 || !services.every(s => s.dataSources.length > 0)) newErrors.dataSources = 'Each service must have a Data Source.';
//     if (services.length === 0 || !services.every(s => s.regions.length > 0)) newErrors.regions = 'Each service must have a Region.';
//     if (services.length === 0 || !services.every(s => s.serviceTypes.length > 0)) newErrors.services = 'Each service must have a Service Type configured.';
//     if (!amount.trim()) newErrors.amount = 'Budget Amount is required.';
//     if (alertRecipients.length === 0) newErrors.alertRecipients = 'At least one Alert Recipient is required.';

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async () => {
//     if (!validateForm() || !currentTenant?.id) return;

//     const createBudgetInput = {
//       tenantId: currentTenant.id,
//       name: budgetName.trim(),
//       description: budgetDescription.trim(),
//       tags: tags.reduce((obj, item) => ({ ...obj, [item.label]: item.value }), {}),
//       provider: provider,
//       period: period.toUpperCase() as 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
//       amount: parseFloat(amount.replace(/,/g, '')),
//       includeCredits: includeCredits,
//       alertThresholds: thresholds.map(t => parseInt(t.percentage, 10)).filter(t => !isNaN(t)),
      
//       userIdsToNotify: alertRecipients.map(recipient => {
//         if (recipient.type === 'role') {
//           return `ROLE:${recipient.data.name.toLowerCase()}`;
//         }
//         return recipient.data.id as string;
//       }),

//       scopeConfigurations: services.map(service => ({
//         dataSourceIds: service.dataSources,
//         projectIds: service.projectIds,
//         regions: service.regions,
//         services: service.serviceTypes,
//         labels: service.label.flatMap((l: LabelFilterPair) =>
//             l.values.map((v: string) => ({ key: l.key, value: v }))
//         ),
//         k8sClusters: service.cluster,
//         k8sNamespaces: service.namespace,
//       })),
//     };

//     try {
//       const result = await createBudget({
//         variables: { input: createBudgetInput },
//       });

//       if (result.data?.createBudget) {
//         navigate('/budget-alerts', {
//           replace: true,
//           state: { budgetAdded: true, budgetName: result.data.createBudget.name }
//         });
//       }
//     } catch (err) {
//       console.error("Failed to create budget:", err);
//       setIsErrorToastOpen(true); 
//     }
//   };

//   const handleAddTag = () => {
//     const newTag: Tag = { id: Date.now(), label: dialogLabel, value: dialogTag };
//     if (newTag.label && newTag.value && !tags.some(tag => tag.label === newTag.label && tag.value === newTag.value)) {
//       setTags(prevTags => [...prevTags, newTag]);
//     }
//     setIsDialogOpen(false);
//     setDialogLabel('');
//     setDialogTag('');
//   };

//   const handleDeleteTag = (tagToDelete: Tag) => {
//     setLastDeletedTag(tagToDelete);
//     setTags(prevTags => prevTags.filter(tag => tag.id !== tagToDelete.id));
//     setIsToastOpen(true);
//   };
  
//   const handleUndo = () => {
//     if (lastDeletedTag) {
//       setTags(prevTags => [...prevTags, lastDeletedTag]);
//       setLastDeletedTag(null);
//       setIsToastOpen(false);
//     }
//   };

//   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') return;
//     setIsToastOpen(false);
//     setLastDeletedTag(null);
//   };
  
//   const handleErrorToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') {
//       return;
//     }
//     setIsErrorToastOpen(false);
//   };

//   const handleCloseDialog = () => {
//     setIsDialogOpen(false);
//     setDialogLabel('');
//     setDialogTag('');
//   };
  
//   return (
//     <Box sx={{ p: { xs: 1, sm: 3 }, height: '100%', boxSizing: 'border-box', backgroundColor: '#F8F9FA' }}>
//       <LabelSuccessToast open={isToastOpen} onClose={handleToastClose} message="Label deleted successfully" onUndo={handleUndo} />
//       <Snackbar
//         open={isErrorToastOpen}
//         autoHideDuration={6000}
//         onClose={handleErrorToastClose}
//         anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
//       >
//         <Alert onClose={handleErrorToastClose} severity="error" variant="filled" sx={{ width: '100%' }}>
//           We are unable to add budget at this moment. Please try again after some time.
//         </Alert>
//       </Snackbar>

//       <Box sx={{ mb: 2 }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: -1 }}>
//           <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: '#333333', ml: -1 }}>
//             <ArrowBackIcon sx={{ width: '20px', height: '20px' }} />
//           </IconButton>
//           <Typography variant="body2" color="#333333" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '14px' }}>
//             Add New Budget
//           </Typography>
//         </Box>
//         <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 2.5, mt: -1 }}>
//           Add budget to Keep your cloud spending on track with a custom budget.
//         </Typography>
//       </Box>
      
//       <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        
//         <BasicDetails
//             budgetName={budgetName}
//             onBudgetNameChange={handleBudgetNameChange}
//             budgetDescription={budgetDescription}
//             onBudgetDescriptionChange={handleBudgetDescriptionChange}
//             tags={tags}
//             handleDeleteTag={handleDeleteTag}
//             setIsDialogOpen={setIsDialogOpen}
//             errors={errors}
//         />

//         <ScopeConfig
//             provider={provider}
//             setProvider={setProvider}
//             services={services}
//             setServices={setServices}
//             getInitialService={getInitialService}
//             errors={errors}
//             period={period} 
//         />

//         <BudgetDetails
//           period={period}
//           setPeriod={setPeriod}
//           currency={currency}
//           setCurrency={setCurrency}
//           amount={amount}
//           setAmount={setAmount}
//           includeCredits={includeCredits}
//           setIncludeCredits={setIncludeCredits}
//           thresholds={thresholds}
//           setThresholds={setThresholds}
//           errors={errors}
//         />

//         <AlertSettings 
//             recipients={alertRecipients}
//             setRecipients={setAlertRecipients}
//             errors={errors}
//             allUsers={allUsers}
//             isLoading={usersLoading}
//             roles={staticRoles}
//         />

//         <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
//           <Box sx={{ display: 'flex', gap: 1 }}>
//             <Button onClick={() => navigate(-1)} sx={{ width: '79px', height: '36px', borderRadius: '8px', backgroundColor: '#F2F7FF', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#E0EFFF' } }}>
//               Cancel
//             </Button>
//             <Button
//               variant="contained"
//               onClick={handleSubmit}
//               disabled={mutationLoading}
//               sx={{ width: '112px', height: '36px', borderRadius: '8px', backgroundColor: '#0064FF', color: 'white', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#0053D9' } }}
//             >
//               {mutationLoading ? <CircularProgress size={24} color="inherit" /> : 'Add Budget'}
//             </Button>
//           </Box>
//         </Box>
//       </Box>

//       <Dialog 
//         open={isDialogOpen} 
//         onClose={handleCloseDialog}
//         PaperProps={{
//           ref: paperRef,
//           sx: {
//             borderRadius: '12px', width: '600px', height: '322px',
//             maxWidth: '600px', overflow: 'hidden',
//             backgroundImage: `url('/textures/Texture.svg'), url('/textures/Gradient_Popup.svg')`,
//             backgroundRepeat: 'no-repeat, no-repeat',
//             backgroundPosition: 'top right, center', backgroundSize: 'auto, cover',
//           }
//         }}
//       >
//        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
//           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//             <img src="/icons/add-filter-icon1.svg" alt="Add" style={{ width: '56px', height: '56px', marginRight: '16px' }} />
//             <Box>
//               <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px', color: '#333333', fontFamily: 'Inter' }}>
//                 Add Label/ Tag
//               </Typography>
//               <Typography variant="body2" sx={{ fontWeight: 400,fontSize: '14px', color: '#666666', fontFamily: 'Inter' }}>
//                 Add label details to setup alerts.
//               </Typography>
//             </Box>
//           </Box>
//           <DialogContent sx={{ p: '0 !important', mt: 7, mb: 3, flexGrow: 1 }}>
//             <Box sx={{ display: 'flex', gap: 2 }}>
//               <Box sx={{ flex: 1 }}>
//                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>
//                   Key<span style={{color: 'red'}}>*</span>
//                 </Typography>
//                 <TextField
//                   fullWidth variant="outlined" placeholder="Enter label"
//                   value={dialogLabel} onChange={(e) => setDialogLabel(e.target.value)} size="small"
//                   InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogLabel && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }}
//                   sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0', }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px', }, }}
//                 />
//               </Box>
//               <Box sx={{ flex: 1 }}>
//                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>
//                   Value<span style={{color: 'red'}}>*</span>
//                 </Typography>
//                 <TextField
//                   fullWidth variant="outlined" placeholder="Enter tag"
//                   value={dialogTag} onChange={(e) => setDialogTag(e.target.value)} size="small"
//                    InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogTag && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }}
//                   sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '& fieldset': { borderColor: '#E0E0E0', }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px',}, }}
//                 />
//               </Box>
//             </Box>
//           </DialogContent>
//           <DialogActions sx={{ p: 0 }}>
//             <Button onClick={handleCloseDialog} sx={{ width:"79px", height:"36px",textTransform: 'none', borderRadius: '8px', color: '#0064FF', backgroundColor: '#F2F7FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}>Cancel</Button>
//             <Button 
//               variant="contained" onClick={handleAddTag} disabled={!dialogLabel || !dialogTag}
//               sx={{ width:"74px", height:"36px",textTransform: 'none', borderRadius: '8px', backgroundColor: '#0064FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}
//             > Add </Button>
//           </DialogActions> 
//         </Box>
//       </Dialog>
//     </Box>
//   );
// };

// export default AddBudgetDialog;

// // import React, { useState, useEffect, useRef } from 'react';
// // import {
// //   Box,
// //   Typography,
// //   Button,
// //   IconButton,
// //   TextField,
// //   Dialog,
// //   DialogActions,
// //   DialogContent,
// //   InputAdornment,
// //   CircularProgress,
// //   Snackbar, 
// //   Alert,   
// // } from '@mui/material';
// // import { useNavigate, useOutletContext } from 'react-router-dom';
// // import { gql, useMutation, useApolloClient } from '@apollo/client';
// // import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// // import CheckIcon from '@mui/icons-material/Check';
// // import BasicDetails from './BasicDetails';
// // import BudgetDetails from './BudgetDetails';
// // import ScopeConfig, { ServiceState } from './ScopeConfig';
// // import { LabelFilterPair } from './LabelFilterMenu';
// // import AlertSettings from './AlertSettings';
// // import LabelSuccessToast from './LabelSuccessToast';

// // // --- GQL MUTATIONS AND QUERIES ---
// // const CREATE_BUDGET_MUTATION = gql`
// //   mutation CreateBudget($input: CreateBudgetInput!) {
// //     createBudget(input: $input) {
// //       id
// //       name
// //     }
// //   }
// // `;

// // const GET_USERS_BY_ROLE = gql`
// //   query GetTenantMembers($tenantId: ID!) {
// //     usersByTenant(tenantId: $tenantId) {
// //       user {
// //         id
// //         name
// //         email
// //         photo
// //       }
// //       roleInTenant
// //     }
// //   }
// // `;

// // // --- TYPE DEFINITIONS ---
// // interface UserData {
// //   id: string;
// //   name: string;
// //   email: string;
// //   role: string;
// //   avatar?: string;
// // }

// // type AlertRecipient = {
// //   data: { id?: string; name: string; [key: string]: any };
// //   type: 'role' | 'user';
// // };

// // interface RoleData {
// //   name: string;
// //   description: string;
// //   icon: string;
// //   color: string;
// // }

// // interface Tag {
// //   label: string;
// //   value: string;
// //   id: string | number;
// // }

// // interface AppOutletContext {
// //   currentTenant?: { id: string; name: string; };
// // }

// // const getInitialService = (): Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] } => ({
// //   dataSources: [],
// //   regions: [],
// //   serviceTypes: [],
// //   label: [],
// //   cluster: [],
// //   namespace: [],
// //   deployment: []
// // });

// // // --- COMPONENT ---
// // const AddBudgetDialog: React.FC = () => {
// //   const navigate = useNavigate();
// //   const { currentTenant } = useOutletContext<AppOutletContext>();
// //   const paperRef = useRef(null);
  
// //   const client = useApolloClient();
// // const staticRoles: RoleData[] = [
// //   { name: 'Admin', description: 'All current/ new users with admin role', icon: '/icons/admin.svg', color: '#FF8900' },
// //   { name: 'Editor', description: 'All current/ new users with editor role', icon: '/icons/editor.svg', color: '#49A7E4' },
// //   { name: 'Viewer', description: 'All current/ new users with viewer role', icon: '/icons/viewer.svg', color: '#666666' },
// // ];
// //   const [createBudget, { loading: mutationLoading }] = useMutation(CREATE_BUDGET_MUTATION);

// //   // --- COMPONENT STATE ---
// //   const [errors, setErrors] = useState<{ [key: string]: string }>({});
// //   const [budgetName, setBudgetName] = useState('');
// //   const [budgetDescription, setBudgetDescription] = useState('');
// //   const [tags, setTags] = useState<Tag[]>([]);
// //   const [provider, setProvider] = useState('');
// //   const [services, setServices] = useState<ServiceState[]>([]);
// //   const [period, setPeriod] = useState<'Monthly' | 'Quarterly' | 'Yearly'>('Monthly');
// //   const [currency, setCurrency] = useState<'INR' | 'USD'>('USD');
// //   const [amount, setAmount] = useState('');
// //   const [includeCredits, setIncludeCredits] = useState(false);
// //   const [thresholds, setThresholds] = useState<{ id: number; percentage: string; }[]>([]);
// //   const [alertRecipients, setAlertRecipients] = useState<AlertRecipient[]>([]);
// //   const [isDialogOpen, setIsDialogOpen] = useState(false);
// //   const [dialogLabel, setDialogLabel] = useState('');
// //   const [dialogTag, setDialogTag] = useState('');
// //   const [isToastOpen, setIsToastOpen] = useState(false);
// //   const [lastDeletedTag, setLastDeletedTag] = useState<Tag | null>(null);
// //   const [allUsers, setAllUsers] = useState<UserData[]>([]);
// //   const [usersLoading, setUsersLoading] = useState(true);
// //   const [isErrorToastOpen, setIsErrorToastOpen] = useState(false); // <-- ADDED: State for the error toast
  
// //   const isBudgetDetailsComplete = period && amount.trim() !== '';

// //   useEffect(() => {
// //     if (!currentTenant?.id) return;

// //     const fetchAllUsers = async () => {
// //       setUsersLoading(true);
// //       const rolesToFetch = ['ADMIN', 'EDITOR', 'VIEWER'];

// //       try {
// //         const promises = rolesToFetch.map(role => 
// //           client.query({
// //             query: GET_USERS_BY_ROLE,
// //             variables: { tenantId: currentTenant.id, roles: [role] }
// //           })
// //         );
        
// //         const results = await Promise.all(promises);        
// //         const combinedUsers = results.flatMap(result => result.data.usersByTenant);
// //         const uniqueUsersMap = new Map();
// //         combinedUsers.forEach(({ user, roleInTenant }) => {
// //             if (user && user.id && !uniqueUsersMap.has(user.id)) {
// //                 uniqueUsersMap.set(user.id, {
// //                     id: user.id,
// //                     name: user.name,
// //                     email: user.email,
// //                     role: roleInTenant, 
// //                     avatar: user.photo   
// //                 });
// //             }
// //         });        
// //         setAllUsers(Array.from(uniqueUsersMap.values()));

// //       } catch (error) {
// //         console.error("Failed to fetch users for dropdown:", error);
// //         setAllUsers([]);
// //       } finally {
// //         setUsersLoading(false);
// //       }
// //     };

// //     fetchAllUsers();
// //   }, [currentTenant?.id, client]);
  
// //   useEffect(() => {
// //     const newErrors = { ...errors };
// //     let hasErrorChanged = false;

// //     if (amount.trim() && newErrors.amount) {
// //         delete newErrors.amount;
// //         hasErrorChanged = true;
// //     }
// //     if (provider && newErrors.provider) {
// //         delete newErrors.provider;
// //         hasErrorChanged = true;
// //     }
// //     if (services.length > 0 && (newErrors.dataSources || newErrors.regions || newErrors.services)) {
// //         delete newErrors.dataSources;
// //         delete newErrors.regions;
// //         delete newErrors.services;
// //         hasErrorChanged = true;
// //     }
// //     if (alertRecipients.length > 0 && newErrors.alertRecipients) {
// //         delete newErrors.alertRecipients;
// //         hasErrorChanged = true;
// //     }

// //     if (hasErrorChanged) {
// //         setErrors(newErrors);
// //     }
// //   }, [amount, provider, services, alertRecipients, errors]);

// //   const handleBudgetNameChange = (name: string) => {
// //     setBudgetName(name);
// //     const newErrors = { ...errors };

// //     if (newErrors.budgetName) {
// //         delete newErrors.budgetName;
// //     }

// //     if (!/^[a-zA-Z0-9\s_-]*$/.test(name)) {
// //       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
// //     }
// //     setErrors(newErrors);
// //   };

// //   const handleBudgetDescriptionChange = (description: string) => {
// //     setBudgetDescription(description);
// //     const newErrors = { ...errors };

// //     if(newErrors.budgetDescription) {
// //         delete newErrors.budgetDescription;
// //     }

// //     if (description.length > 200) {
// //       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
// //     }
// //     setErrors(newErrors);
// //   };

// //   const validateForm = () => {
// //     const newErrors: { [key: string]: string } = {};
    
// //     const trimmedName = budgetName.trim();
// //     if (!trimmedName) {
// //       newErrors.budgetName = 'Budget Name is required.';
// //     } else if (trimmedName.length < 3) {
// //       newErrors.budgetName = 'Name must be at least 3 characters.';
// //     } else if (trimmedName.length > 50) {
// //       newErrors.budgetName = 'Name cannot exceed 50 characters.';
// //     } else if (!/^[a-zA-Z0-9\s_-]+$/.test(trimmedName)) {
// //       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
// //     }

// //     const trimmedDescription = budgetDescription.trim();
// //     if (!trimmedDescription) {
// //       newErrors.budgetDescription = 'Budget Description is required.';
// //     } else if (trimmedDescription.length > 200) {
// //       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
// //     }

// //     if (!provider) newErrors.provider = 'A Cloud Provider must be selected.';
// //     if (services.length === 0 || !services.every(s => s.dataSources.length > 0)) newErrors.dataSources = 'Each service must have a Data Source.';
// //     if (services.length === 0 || !services.every(s => s.regions.length > 0)) newErrors.regions = 'Each service must have a Region.';
// //     if (services.length === 0 || !services.every(s => s.serviceTypes.length > 0)) newErrors.services = 'Each service must have a Service Type configured.';
// //     if (!amount.trim()) newErrors.amount = 'Budget Amount is required.';
// //     if (alertRecipients.length === 0) newErrors.alertRecipients = 'At least one Alert Recipient is required.';

// //     setErrors(newErrors);
// //     return Object.keys(newErrors).length === 0;
// //   };

// //   const handleSubmit = async () => {
// //     if (!validateForm() || !currentTenant?.id) return;

// //     const createBudgetInput = {
// //       tenantId: currentTenant.id,
// //       name: budgetName.trim(),
// //       description: budgetDescription.trim(),
// //       tags: tags.reduce((obj, item) => ({ ...obj, [item.label]: item.value }), {}),
// //       provider: provider,
// //       period: period.toUpperCase() as 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
// //       amount: parseFloat(amount.replace(/,/g, '')),
// //       includeCredits: includeCredits,
// //       alertThresholds: thresholds.map(t => parseInt(t.percentage, 10)).filter(t => !isNaN(t)),
      
// //       userIdsToNotify: alertRecipients.map(recipient => {
// //         if (recipient.type === 'role') {
// //           return `ROLE:${recipient.data.name.toLowerCase()}`;
// //         }
// //         return recipient.data.id as string;
// //       }),

// //       scopeConfigurations: services.map(service => ({
// //         dataSourceIds: service.dataSources,
// //         regions: service.regions,
// //         services: service.serviceTypes,
// //         labels: service.label.flatMap((l: LabelFilterPair) =>
// //             l.values.map((v: string) => ({ key: l.key, value: v }))
// //         ),
// //         k8sClusters: service.cluster,
// //         k8sNamespaces: service.namespace,
// //       })),
// //     };

// //     try {
// //       const result = await createBudget({
// //         variables: { input: createBudgetInput },
// //       });

// //       if (result.data?.createBudget) {
// //         navigate('/budget-alerts', {
// //           replace: true,
// //           state: { budgetAdded: true, budgetName: result.data.createBudget.name }
// //         });
// //       }
// //     } catch (err) {
// //       console.error("Failed to create budget:", err);
// //       setIsErrorToastOpen(true); 
// //     }
// //   };

// //   const handleAddTag = () => {
// //     const newTag: Tag = { id: Date.now(), label: dialogLabel, value: dialogTag };
// //     if (newTag.label && newTag.value && !tags.some(tag => tag.label === newTag.label && tag.value === newTag.value)) {
// //       setTags(prevTags => [...prevTags, newTag]);
// //     }
// //     setIsDialogOpen(false);
// //     setDialogLabel('');
// //     setDialogTag('');
// //   };

// //   const handleDeleteTag = (tagToDelete: Tag) => {
// //     setLastDeletedTag(tagToDelete);
// //     setTags(prevTags => prevTags.filter(tag => tag.id !== tagToDelete.id));
// //     setIsToastOpen(true);
// //   };
  
// //   const handleUndo = () => {
// //     if (lastDeletedTag) {
// //       setTags(prevTags => [...prevTags, lastDeletedTag]);
// //       setLastDeletedTag(null);
// //       setIsToastOpen(false);
// //     }
// //   };

// //   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
// //     if (reason === 'clickaway') return;
// //     setIsToastOpen(false);
// //     setLastDeletedTag(null);
// //   };
  
// //   // <-- ADDED: Handler to close the error toast
// //   const handleErrorToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
// //     if (reason === 'clickaway') {
// //       return;
// //     }
// //     setIsErrorToastOpen(false);
// //   };

// //   const handleCloseDialog = () => {
// //     setIsDialogOpen(false);
// //     setDialogLabel('');
// //     setDialogTag('');
// //   };
  
// //   return (
// //     <Box sx={{ p: { xs: 1, sm: 3 }, height: '100%', boxSizing: 'border-box', backgroundColor: '#F8F9FA' }}>
// //       <LabelSuccessToast open={isToastOpen} onClose={handleToastClose} message="Label deleted successfully" onUndo={handleUndo} />
// //       <Snackbar
// //         open={isErrorToastOpen}
// //         autoHideDuration={6000}
// //         onClose={handleErrorToastClose}
// //         anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
// //       >
// //         <Alert onClose={handleErrorToastClose} severity="error" variant="filled" sx={{ width: '100%' }}>
// //           We are unable to add budget at this moment. Please try again after some time.
// //         </Alert>
// //       </Snackbar>

// //       <Box sx={{ mb: 2 }}>
// //         <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: -1 }}>
// //           <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: '#333333', ml: -1 }}>
// //             <ArrowBackIcon sx={{ width: '20px', height: '20px' }} />
// //           </IconButton>
// //           <Typography variant="body2" color="#333333" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '14px' }}>
// //             Add New Budget
// //           </Typography>
// //         </Box>
// //         <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 2.5, mt: -1 }}>
// //           Add budget to Keep your cloud spending on track with a custom budget.
// //         </Typography>
// //       </Box>
      
// //       <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        
// //         <BasicDetails
// //             budgetName={budgetName}
// //             onBudgetNameChange={handleBudgetNameChange}
// //             budgetDescription={budgetDescription}
// //             onBudgetDescriptionChange={handleBudgetDescriptionChange}
// //             tags={tags}
// //             handleDeleteTag={handleDeleteTag}
// //             setIsDialogOpen={setIsDialogOpen}
// //             errors={errors}
// //         />

// //         <BudgetDetails
// //           period={period}
// //           setPeriod={setPeriod}
// //           currency={currency}
// //           setCurrency={setCurrency}
// //           amount={amount}
// //           setAmount={setAmount}
// //           includeCredits={includeCredits}
// //           setIncludeCredits={setIncludeCredits}
// //           thresholds={thresholds}
// //           setThresholds={setThresholds}
// //           errors={errors}
// //         />

// //         <ScopeConfig
// //             provider={provider}
// //             setProvider={setProvider}
// //             services={services}
// //             setServices={setServices}
// //             getInitialService={getInitialService}
// //             errors={errors}
// //             disabled={!isBudgetDetailsComplete}
// //             period={period} 
// //         />

// //         <AlertSettings 
// //             recipients={alertRecipients}
// //             setRecipients={setAlertRecipients}
// //             errors={errors}
// //             allUsers={allUsers}
// //             isLoading={usersLoading}
// //             roles={staticRoles}
// //         />

// //         <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
// //           <Box sx={{ display: 'flex', gap: 1 }}>
// //             <Button onClick={() => navigate(-1)} sx={{ width: '79px', height: '36px', borderRadius: '8px', backgroundColor: '#F2F7FF', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#E0EFFF' } }}>
// //               Cancel
// //             </Button>
// //             <Button
// //               variant="contained"
// //               onClick={handleSubmit}
// //               disabled={mutationLoading}
// //               sx={{ width: '112px', height: '36px', borderRadius: '8px', backgroundColor: '#0064FF', color: 'white', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#0053D9' } }}
// //             >
// //               {mutationLoading ? <CircularProgress size={24} color="inherit" /> : 'Add Budget'}
// //             </Button>
// //           </Box>
// //         </Box>
// //       </Box>

// //       <Dialog 
// //         open={isDialogOpen} 
// //         onClose={handleCloseDialog}
// //         PaperProps={{
// //           ref: paperRef,
// //           sx: {
// //             borderRadius: '12px', width: '600px', height: '322px',
// //             maxWidth: '600px', overflow: 'hidden',
// //             backgroundImage: `url('/textures/Texture.svg'), url('/textures/Gradient_Popup.svg')`,
// //             backgroundRepeat: 'no-repeat, no-repeat',
// //             backgroundPosition: 'top right, center', backgroundSize: 'auto, cover',
// //           }
// //         }}
// //       >
// //        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
// //           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
// //             <img src="/icons/add-filter-icon1.svg" alt="Add" style={{ width: '56px', height: '56px', marginRight: '16px' }} />
// //             <Box>
// //               <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px', color: '#333333', fontFamily: 'Inter' }}>
// //                 Add Label/ Tag
// //               </Typography>
// //               <Typography variant="body2" sx={{ fontWeight: 400,fontSize: '14px', color: '#666666', fontFamily: 'Inter' }}>
// //                 Add label details to setup alerts.
// //               </Typography>
// //             </Box>
// //           </Box>
// //           <DialogContent sx={{ p: '0 !important', mt: 7, mb: 3, flexGrow: 1 }}>
// //             <Box sx={{ display: 'flex', gap: 2 }}>
// //               <Box sx={{ flex: 1 }}>
// //                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>
// //                   Key<span style={{color: 'red'}}>*</span>
// //                 </Typography>
// //                 <TextField
// //                   fullWidth variant="outlined" placeholder="Enter label"
// //                   value={dialogLabel} onChange={(e) => setDialogLabel(e.target.value)} size="small"
// //                   InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogLabel && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }}
// //                   sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0', }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px', }, }}
// //                 />
// //               </Box>
// //               <Box sx={{ flex: 1 }}>
// //                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>
// //                   Value<span style={{color: 'red'}}>*</span>
// //                 </Typography>
// //                 <TextField
// //                   fullWidth variant="outlined" placeholder="Enter tag"
// //                   value={dialogTag} onChange={(e) => setDialogTag(e.target.value)} size="small"
// //                    InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogTag && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }}
// //                   sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '& fieldset': { borderColor: '#E0E0E0', }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px',}, }}
// //                 />
// //               </Box>
// //             </Box>
// //           </DialogContent>
// //           <DialogActions sx={{ p: 0 }}>
// //             <Button onClick={handleCloseDialog} sx={{ width:"79px", height:"36px",textTransform: 'none', borderRadius: '8px', color: '#0064FF', backgroundColor: '#F2F7FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}>Cancel</Button>
// //             <Button 
// //               variant="contained" onClick={handleAddTag} disabled={!dialogLabel || !dialogTag}
// //               sx={{ width:"74px", height:"36px",textTransform: 'none', borderRadius: '8px', backgroundColor: '#0064FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}
// //             > Add </Button>
// //           </DialogActions> 
// //         </Box>
// //       </Dialog>
// //     </Box>
// //   );
// // };

// // export default AddBudgetDialog;

// import React, { useState, useEffect, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   Button,
//   IconButton,
//   TextField,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   InputAdornment,
//   CircularProgress,
//   Snackbar,
//   Alert,
// } from '@mui/material';
// import { useNavigate, useOutletContext } from 'react-router-dom';
// import { gql, useMutation, useApolloClient } from '@apollo/client';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import CheckIcon from '@mui/icons-material/Check';
// import BasicDetails from './BasicDetails';
// import BudgetDetails from './BudgetDetails';
// // Import ServiceState to ensure type consistency
// import ScopeConfig, { ServiceState } from './ScopeConfig';
// import { LabelFilterPair } from './LabelFilterMenu';
// import AlertSettings from './AlertSettings';
// import LabelSuccessToast from './LabelSuccessToast';

// // --- GQL MUTATIONS AND QUERIES ---
// const CREATE_BUDGET_MUTATION = gql`
//   mutation CreateBudget($input: CreateBudgetInput!) {
//     createBudget(input: $input) {
//       id
//       name
//     }
//   }
// `;

// const GET_USERS_BY_ROLE = gql`
//   query GetTenantMembers($tenantId: ID!) {
//     usersByTenant(tenantId: $tenantId) {
//       user {
//         id
//         name
//         email
//         photo
//       }
//       roleInTenant
//     }
//   }
// `;

// // --- TYPE DEFINITIONS ---
// interface UserData {
//   id: string;
//   name: string;
//   email: string;
//   role: string;
//   avatar?: string;
// }

// type AlertRecipient = {
//   data: { id?: string; name: string; [key: string]: any };
//   type: 'role' | 'user';
// };

// interface RoleData {
//   name: string;
//   description: string;
//   icon: string;
//   color: string;
// }

// interface Tag {
//   label: string;
//   value: string;
//   id: string | number;
// }

// interface AppOutletContext {
//   currentTenant?: { id: string; name: string; };
// }

// // --- FIXED ---
// const getInitialService = (): Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] } => ({
//   dataSources: [],
//   projectIds: [], // <-- FIX: This line was missing
//   regions: [],
//   serviceTypes: [],
//   label: [],
//   cluster: [],
//   namespace: [],
//   deployment: []
// });

// // --- COMPONENT ---
// const AddBudgetDialog: React.FC = () => {
//   const navigate = useNavigate();
//   const { currentTenant } = useOutletContext<AppOutletContext>();
//   const paperRef = useRef(null);
  
//   const client = useApolloClient();
// const staticRoles: RoleData[] = [
//   { name: 'Admin', description: 'All current/ new users with admin role', icon: '/icons/admin.svg', color: '#FF8900' },
//   { name: 'Editor', description: 'All current/ new users with editor role', icon: '/icons/editor.svg', color: '#49A7E4' },
//   { name: 'Viewer', description: 'All current/ new users with viewer role', icon: '/icons/viewer.svg', color: '#666666' },
// ];
//   const [createBudget, { loading: mutationLoading }] = useMutation(CREATE_BUDGET_MUTATION);

//   // --- COMPONENT STATE ---
//   const [errors, setErrors] = useState<{ [key: string]: string }>({});
//   const [budgetName, setBudgetName] = useState('');
//   const [budgetDescription, setBudgetDescription] = useState('');
//   const [tags, setTags] = useState<Tag[]>([]);
//   const [provider, setProvider] = useState('');
//   const [services, setServices] = useState<ServiceState[]>([]);
//   const [period, setPeriod] = useState<'Monthly' | 'Quarterly' | 'Yearly'>('Monthly');
//   const [currency, setCurrency] = useState<'INR' | 'USD'>('USD');
//   const [amount, setAmount] = useState('');
//   const [includeCredits, setIncludeCredits] = useState(false);
//   const [thresholds, setThresholds] = useState<{ id: number; percentage: string; }[]>([]);
//   const [alertRecipients, setAlertRecipients] = useState<AlertRecipient[]>([]);
//   const [isDialogOpen, setIsDialogOpen] = useState(false);
//   const [dialogLabel, setDialogLabel] = useState('');
//   const [dialogTag, setDialogTag] = useState('');
//   const [isToastOpen, setIsToastOpen] = useState(false);
//   const [lastDeletedTag, setLastDeletedTag] = useState<Tag | null>(null);
//   const [allUsers, setAllUsers] = useState<UserData[]>([]);
//   const [usersLoading, setUsersLoading] = useState(true);
//   const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);
  
//   const isBudgetDetailsComplete = period && amount.trim() !== '';

//   useEffect(() => {
//     if (!currentTenant?.id) return;

//     const fetchAllUsers = async () => {
//       setUsersLoading(true);
//       const rolesToFetch = ['ADMIN', 'EDITOR', 'VIEWER'];

//       try {
//         const promises = rolesToFetch.map(role => 
//           client.query({
//             query: GET_USERS_BY_ROLE,
//             variables: { tenantId: currentTenant.id, roles: [role] }
//           })
//         );
        
//         const results = await Promise.all(promises);        
//         const combinedUsers = results.flatMap(result => result.data.usersByTenant);
//         const uniqueUsersMap = new Map();
//         combinedUsers.forEach(({ user, roleInTenant }) => {
//             if (user && user.id && !uniqueUsersMap.has(user.id)) {
//                 uniqueUsersMap.set(user.id, {
//                     id: user.id,
//                     name: user.name,
//                     email: user.email,
//                     role: roleInTenant, 
//                     avatar: user.photo   
//                 });
//             }
//         });        
//         setAllUsers(Array.from(uniqueUsersMap.values()));

//       } catch (error) {
//         console.error("Failed to fetch users for dropdown:", error);
//         setAllUsers([]);
//       } finally {
//         setUsersLoading(false);
//       }
//     };

//     fetchAllUsers();
//   }, [currentTenant?.id, client]);
  
//   useEffect(() => {
//     const newErrors = { ...errors };
//     let hasErrorChanged = false;

//     if (amount.trim() && newErrors.amount) {
//         delete newErrors.amount;
//         hasErrorChanged = true;
//     }
//     if (provider && newErrors.provider) {
//         delete newErrors.provider;
//         hasErrorChanged = true;
//     }
//     if (services.length > 0 && (newErrors.dataSources || newErrors.regions || newErrors.services)) {
//         delete newErrors.dataSources;
//         delete newErrors.regions;
//         delete newErrors.services;
//         hasErrorChanged = true;
//     }
//     if (alertRecipients.length > 0 && newErrors.alertRecipients) {
//         delete newErrors.alertRecipients;
//         hasErrorChanged = true;
//     }

//     if (hasErrorChanged) {
//         setErrors(newErrors);
//     }
//   }, [amount, provider, services, alertRecipients, errors]);

//   const handleBudgetNameChange = (name: string) => {
//     setBudgetName(name);
//     const newErrors = { ...errors };

//     if (newErrors.budgetName) {
//         delete newErrors.budgetName;
//     }

//     if (!/^[a-zA-Z0-9\s_-]*$/.test(name)) {
//       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
//     }
//     setErrors(newErrors);
//   };

//   const handleBudgetDescriptionChange = (description: string) => {
//     setBudgetDescription(description);
//     const newErrors = { ...errors };

//     if(newErrors.budgetDescription) {
//         delete newErrors.budgetDescription;
//     }

//     if (description.length > 200) {
//       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
//     }
//     setErrors(newErrors);
//   };

//   const validateForm = () => {
//     const newErrors: { [key: string]: string } = {};
    
//     const trimmedName = budgetName.trim();
//     if (!trimmedName) {
//       newErrors.budgetName = 'Budget Name is required.';
//     } else if (trimmedName.length < 3) {
//       newErrors.budgetName = 'Name must be at least 3 characters.';
//     } else if (trimmedName.length > 50) {
//       newErrors.budgetName = 'Name cannot exceed 50 characters.';
//     } else if (!/^[a-zA-Z0-9\s_-]+$/.test(trimmedName)) {
//       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
//     }

//     const trimmedDescription = budgetDescription.trim();
//     if (!trimmedDescription) {
//       newErrors.budgetDescription = 'Budget Description is required.';
//     } else if (trimmedDescription.length > 200) {
//       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
//     }

//     if (!provider) newErrors.provider = 'A Cloud Provider must be selected.';
//     if (services.length === 0 || !services.every(s => s.dataSources.length > 0)) newErrors.dataSources = 'Each service must have a Data Source.';
//     if (services.length === 0 || !services.every(s => s.regions.length > 0)) newErrors.regions = 'Each service must have a Region.';
//     if (services.length === 0 || !services.every(s => s.serviceTypes.length > 0)) newErrors.services = 'Each service must have a Service Type configured.';
//     if (!amount.trim()) newErrors.amount = 'Budget Amount is required.';
//     if (alertRecipients.length === 0) newErrors.alertRecipients = 'At least one Alert Recipient is required.';

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async () => {
//     if (!validateForm() || !currentTenant?.id) return;

//     const createBudgetInput = {
//       tenantId: currentTenant.id,
//       name: budgetName.trim(),
//       description: budgetDescription.trim(),
//       tags: tags.reduce((obj, item) => ({ ...obj, [item.label]: item.value }), {}),
//       provider: provider,
//       period: period.toUpperCase() as 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
//       amount: parseFloat(amount.replace(/,/g, '')),
//       includeCredits: includeCredits,
//       alertThresholds: thresholds.map(t => parseInt(t.percentage, 10)).filter(t => !isNaN(t)),
      
//       userIdsToNotify: alertRecipients.map(recipient => {
//         if (recipient.type === 'role') {
//           return `ROLE:${recipient.data.name.toLowerCase()}`;
//         }
//         return recipient.data.id as string;
//       }),

//       scopeConfigurations: services.map(service => ({
//         dataSourceIds: service.dataSources,
//         projectIds: service.projectIds,
//         regions: service.regions,
//         services: service.serviceTypes,
//         labels: service.label.flatMap((l: LabelFilterPair) =>
//             l.values.map((v: string) => ({ key: l.key, value: v }))
//         ),
//         k8sClusters: service.cluster,
//         k8sNamespaces: service.namespace,
//       })),
//     };

//     try {
//       const result = await createBudget({
//         variables: { input: createBudgetInput },
//       });

//       if (result.data?.createBudget) {
//         navigate('/budget-alerts', {
//           replace: true,
//           state: { budgetAdded: true, budgetName: result.data.createBudget.name }
//         });
//       }
//     } catch (err) {
//       console.error("Failed to create budget:", err);
//       setIsErrorToastOpen(true); 
//     }
//   };

//   const handleAddTag = () => {
//     const newTag: Tag = { id: Date.now(), label: dialogLabel, value: dialogTag };
//     if (newTag.label && newTag.value && !tags.some(tag => tag.label === newTag.label && tag.value === newTag.value)) {
//       setTags(prevTags => [...prevTags, newTag]);
//     }
//     setIsDialogOpen(false);
//     setDialogLabel('');
//     setDialogTag('');
//   };

//   const handleDeleteTag = (tagToDelete: Tag) => {
//     setLastDeletedTag(tagToDelete);
//     setTags(prevTags => prevTags.filter(tag => tag.id !== tagToDelete.id));
//     setIsToastOpen(true);
//   };
  
//   const handleUndo = () => {
//     if (lastDeletedTag) {
//       setTags(prevTags => [...prevTags, lastDeletedTag]);
//       setLastDeletedTag(null);
//       setIsToastOpen(false);
//     }
//   };

//   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') return;
//     setIsToastOpen(false);
//     setLastDeletedTag(null);
//   };
  
//   const handleErrorToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') {
//       return;
//     }
//     setIsErrorToastOpen(false);
//   };

//   const handleCloseDialog = () => {
//     setIsDialogOpen(false);
//     setDialogLabel('');
//     setDialogTag('');
//   };
  
//   return (
//     <Box sx={{ p: { xs: 1, sm: 3 }, height: '100%', boxSizing: 'border-box', backgroundColor: '#F8F9FA' }}>
//       <LabelSuccessToast open={isToastOpen} onClose={handleToastClose} message="Label deleted successfully" onUndo={handleUndo} />
//       <Snackbar
//         open={isErrorToastOpen}
//         autoHideDuration={6000}
//         onClose={handleErrorToastClose}
//         anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
//       >
//         <Alert onClose={handleErrorToastClose} severity="error" variant="filled" sx={{ width: '100%' }}>
//           We are unable to add budget at this moment. Please try again after some time.
//         </Alert>
//       </Snackbar>

//       <Box sx={{ mb: 2 }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: -1 }}>
//           <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: '#333333', ml: -1 }}>
//             <ArrowBackIcon sx={{ width: '20px', height: '20px' }} />
//           </IconButton>
//           <Typography variant="body2" color="#333333" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '14px' }}>
//             Add New Budget
//           </Typography>
//         </Box>
//         <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 2.5, mt: -1 }}>
//           Add budget to Keep your cloud spending on track with a custom budget.
//         </Typography>
//       </Box>
      
//       <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        
//         <BasicDetails
//             budgetName={budgetName}
//             onBudgetNameChange={handleBudgetNameChange}
//             budgetDescription={budgetDescription}
//             onBudgetDescriptionChange={handleBudgetDescriptionChange}
//             tags={tags}
//             handleDeleteTag={handleDeleteTag}
//             setIsDialogOpen={setIsDialogOpen}
//             errors={errors}
//         />

//         <BudgetDetails
//           period={period}
//           setPeriod={setPeriod}
//           currency={currency}
//           setCurrency={setCurrency}
//           amount={amount}
//           setAmount={setAmount}
//           includeCredits={includeCredits}
//           setIncludeCredits={setIncludeCredits}
//           thresholds={thresholds}
//           setThresholds={setThresholds}
//           errors={errors}
//         />

//         <ScopeConfig
//             provider={provider}
//             setProvider={setProvider}
//             services={services}
//             setServices={setServices}
//             getInitialService={getInitialService}
//             errors={errors}
//             disabled={!isBudgetDetailsComplete}
//             period={period} 
//         />

//         <AlertSettings 
//             recipients={alertRecipients}
//             setRecipients={setAlertRecipients}
//             errors={errors}
//             allUsers={allUsers}
//             isLoading={usersLoading}
//             roles={staticRoles}
//         />

//         <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
//           <Box sx={{ display: 'flex', gap: 1 }}>
//             <Button onClick={() => navigate(-1)} sx={{ width: '79px', height: '36px', borderRadius: '8px', backgroundColor: '#F2F7FF', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#E0EFFF' } }}>
//               Cancel
//             </Button>
//             <Button
//               variant="contained"
//               onClick={handleSubmit}
//               disabled={mutationLoading}
//               sx={{ width: '112px', height: '36px', borderRadius: '8px', backgroundColor: '#0064FF', color: 'white', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#0053D9' } }}
//             >
//               {mutationLoading ? <CircularProgress size={24} color="inherit" /> : 'Add Budget'}
//             </Button>
//           </Box>
//         </Box>
//       </Box>

//       <Dialog 
//         open={isDialogOpen} 
//         onClose={handleCloseDialog}
//         PaperProps={{
//           ref: paperRef,
//           sx: {
//             borderRadius: '12px', width: '600px', height: '322px',
//             maxWidth: '600px', overflow: 'hidden',
//             backgroundImage: `url('/textures/Texture.svg'), url('/textures/Gradient_Popup.svg')`,
//             backgroundRepeat: 'no-repeat, no-repeat',
//             backgroundPosition: 'top right, center', backgroundSize: 'auto, cover',
//           }
//         }}
//       >
//        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
//           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//             <img src="/icons/add-filter-icon1.svg" alt="Add" style={{ width: '56px', height: '56px', marginRight: '16px' }} />
//             <Box>
//               <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px', color: '#333333', fontFamily: 'Inter' }}>
//                 Add Label/ Tag
//               </Typography>
//               <Typography variant="body2" sx={{ fontWeight: 400,fontSize: '14px', color: '#666666', fontFamily: 'Inter' }}>
//                 Add label details to setup alerts.
//               </Typography>
//             </Box>
//           </Box>
//           <DialogContent sx={{ p: '0 !important', mt: 7, mb: 3, flexGrow: 1 }}>
//             <Box sx={{ display: 'flex', gap: 2 }}>
//               <Box sx={{ flex: 1 }}>
//                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>
//                   Key<span style={{color: 'red'}}>*</span>
//                 </Typography>
//                 <TextField
//                   fullWidth variant="outlined" placeholder="Enter label"
//                   value={dialogLabel} onChange={(e) => setDialogLabel(e.target.value)} size="small"
//                   InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogLabel && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }}
//                   sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0', }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px', }, }}
//                 />
//               </Box>
//               <Box sx={{ flex: 1 }}>
//                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>
//                   Value<span style={{color: 'red'}}>*</span>
//                 </Typography>
//                 <TextField
//                   fullWidth variant="outlined" placeholder="Enter tag"
//                   value={dialogTag} onChange={(e) => setDialogTag(e.target.value)} size="small"
//                    InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogTag && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }}
//                   sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '& fieldset': { borderColor: '#E0E0E0', }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px',}, }}
//                 />
//               </Box>
//             </Box>
//           </DialogContent>
//           <DialogActions sx={{ p: 0 }}>
//             <Button onClick={handleCloseDialog} sx={{ width:"79px", height:"36px",textTransform: 'none', borderRadius: '8px', color: '#0064FF', backgroundColor: '#F2F7FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}>Cancel</Button>
//             <Button 
//               variant="contained" onClick={handleAddTag} disabled={!dialogLabel || !dialogTag}
//               sx={{ width:"74px", height:"36px",textTransform: 'none', borderRadius: '8px', backgroundColor: '#0064FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}
//             > Add </Button>
//           </DialogActions> 
//         </Box>
//       </Dialog>
//     </Box>
//   );
// };

// export default AddBudgetDialog;
